import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import RecommendationCard from "./RecommendationCard";
import api from "../axiosConfig";
import CustomLoader from "./CustomLoader";
import SignUpToContinueModal from "./SignUpToContinueModal";
import { useGuestPreview } from "../context/GuestPreviewContext";
import {
  getAuthRecommendationsCacheSnapshot,
  writeRecommendationsPageCache,
} from "../utils/recommendationsPageCache";

const GUEST_ID_KEY = "gradconnect_guest_id";
const GUEST_INTERESTS_KEY = "gradconnect_guest_research_interests";

const Recommendations = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isGuestPreview, researchInterestsFromHome } = useGuestPreview();

  const guestPreview =
    isGuestPreview || location.state?.guestPreview === true;

  // Hydrate from session cache on first paint so returning from Saved/Search doesn't flash "Loading…"
  const [initialSnap] = useState(() =>
    getAuthRecommendationsCacheSnapshot(guestPreview)
  );
  // Prefetched list from Initialize flow (Option B) — same key as guest preview for consistency
  const navInitialRecs =
    !guestPreview &&
    Array.isArray(location.state?.initialRecommendations) &&
    location.state.initialRecommendations.length > 0
      ? location.state.initialRecommendations
      : null;

  const skipFetchRef = useRef(
    !!(initialSnap?.recommendations?.length || navInitialRecs?.length)
  );
  const [recommendations, setRecommendations] = useState(() => {
    if (initialSnap?.recommendations?.length) return initialSnap.recommendations;
    if (navInitialRecs?.length) return navInitialRecs;
    return [];
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(() => {
    const fromSnap = initialSnap?.recommendations?.length;
    const fromNav = navInitialRecs?.length;
    return !(fromSnap || fromNav);
  });
  const [interestedProfessors, setInterestedProfessors] = useState(
    () => initialSnap?.interestedProfessors ?? new Set()
  );
  const [savedProfessors, setSavedProfessors] = useState(() => {
    if (initialSnap?.recommendations?.length) {
      return initialSnap.savedProfessors ?? new Set();
    }
    if (navInitialRecs?.length) {
      return new Set(
        navInitialRecs.filter((prof) => prof.saved).map((prof) => prof.id)
      );
    }
    return new Set();
  });
  const [recentProfessorIds, setRecentProfessorIds] = useState(() => {
    if (initialSnap?.recommendations?.length) {
      return initialSnap.recentProfessorIds ?? [];
    }
    if (navInitialRecs?.length) {
      return navInitialRecs.map((p) => p.id);
    }
    return [];
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingScrollY, setPendingScrollY] = useState(
    () =>
      typeof initialSnap?.scrollY === "number" ? initialSnap.scrollY : null
  );
  const observer = useRef(null);
  const cacheSnapshotRef = useRef({});

  const researchInterestsForModal =
    researchInterestsFromHome ||
    location.state?.researchInterests ||
    sessionStorage.getItem(GUEST_INTERESTS_KEY) ||
    "";

  // Check initialization (authenticated only)
  useEffect(() => {
    if (guestPreview) {
      return;
    }
    const checkInitialization = async () => {
      try {
        const response = await api.get("/get-whether-initialization-complete/");
        if (!response.data.initialization_complete) {
          navigate("/initialize-recommendations");
        }
      } catch (error) {
        console.error("Error checking initialization:", error);
        setErrorMessage("Failed to check initialization status. Please try again.");
      }
    };

    checkInitialization();
  }, [navigate, guestPreview]);

  // Guest preview: load from state or API
  useEffect(() => {
    if (!guestPreview) return;

    const run = async () => {
      setLoading(true);
      setErrorMessage("");
      try {
        const fromState = location.state?.initialRecommendations;
        if (fromState && Array.isArray(fromState) && fromState.length > 0) {
          setRecommendations(fromState);
          setRecentProfessorIds(fromState.map((p) => p.id));
          setSavedProfessors(new Set());
        } else {
          const guestId =
            location.state?.guestId || sessionStorage.getItem(GUEST_ID_KEY);
          if (!guestId) {
            setErrorMessage("Preview expired. Try again from the home page.");
            setRecommendations([]);
          } else {
            const response = await api.get("/guest/preview-recommendations/", {
              params: { guest_id: guestId },
            });
            setRecommendations(response.data);
            setRecentProfessorIds(response.data.map((p) => p.id));
            setSavedProfessors(new Set());
          }
        }
      } catch (e) {
        setErrorMessage(
          e.response?.data?.error || "Could not load preview. Go back to home and try again."
        );
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [guestPreview, location.state]);

  const refreshAuthenticatedRecommendations = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const response = await api.get("/retrieve-recommendations/");
      setRecommendations(response.data);
      const fetchedProfessorIds = response.data.map((prof) => prof.id);
      setRecentProfessorIds(fetchedProfessorIds);
      const savedSet = new Set(
        response.data.filter((prof) => prof.saved).map((prof) => prof.id)
      );
      setSavedProfessors(savedSet);
    } catch (error) {
      setErrorMessage("Failed to fetch recommendations. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Authenticated: fetch recommendations unless restored from session cache
  useEffect(() => {
    if (guestPreview) return;
    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      return;
    }
    refreshAuthenticatedRecommendations();
  }, [guestPreview, refreshAuthenticatedRecommendations]);

  // When majors/schools filters change in Settings, reload the current batch
  useEffect(() => {
    if (guestPreview) return;
    const handler = () => {
      refreshAuthenticatedRecommendations();
    };
    window.addEventListener("gradconnect:filters-updated", handler);
    return () => window.removeEventListener("gradconnect:filters-updated", handler);
  }, [guestPreview, refreshAuthenticatedRecommendations]);

  // Persist recommendations view when leaving the page (same tab session)
  useEffect(() => {
    return () => {
      if (guestPreview) return;
      const s = cacheSnapshotRef.current;
      writeRecommendationsPageCache({
        recommendations: s.recommendations,
        interestedProfessors: [...s.interestedProfessors],
        savedProfessors: [...s.savedProfessors],
        recentProfessorIds: s.recentProfessorIds,
        scrollY:
          window.scrollY ||
          window.pageYOffset ||
          document.documentElement.scrollTop ||
          0,
      });
    };
  }, [guestPreview]);

  // Restore scroll position after cached content paints
  useEffect(() => {
    if (pendingScrollY === null) return;
    if (loading && recommendations.length === 0) return;
    const y = pendingScrollY;
    setPendingScrollY(null);
    requestAnimationFrame(() => {
      window.scrollTo(0, y);
    });
  }, [loading, pendingScrollY, recommendations.length]);

  const fetchMoreRecommendations = useCallback(async () => {
    if (guestPreview) {
      setShowAuthModal(true);
      return;
    }
    setLoadingMore(true);
    await new Promise((resolve) => setTimeout(resolve, 300));

    try {
      const professorIds = recentProfessorIds;

      const response = await api.post("/load-more-recommendations/", {
        professor_ids: professorIds,
      });

      if (response.data.alert) {
        setErrorMessage(response.data.alert);
      } else {
        setRecommendations((prev) => [...prev, ...response.data]);
        const newProfessorIds = response.data.map((prof) => prof.id);
        setRecentProfessorIds(newProfessorIds);
      }
    } catch (error) {
      setErrorMessage("Failed to load more recommendations. Please try again.");
    } finally {
      setLoadingMore(false);
    }
  }, [recentProfessorIds, guestPreview]);

  useEffect(() => {
    if ((loading && recommendations.length === 0) || loadingMore || guestPreview)
      return;

    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 1.0,
    };

    observer.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        fetchMoreRecommendations();
      }
    }, options);

    const target = document.querySelector("#scroll-anchor");
    if (target) observer.current.observe(target);

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [
    fetchMoreRecommendations,
    loading,
    loadingMore,
    guestPreview,
    recommendations.length,
  ]);

  const markInterested = async (professorId) => {
    if (guestPreview) return;
    try {
      await api.post("/handle-interaction/", {
        professor_id: professorId,
        interaction_type: "interested",
      });
      setInterestedProfessors((prev) => new Set(prev).add(professorId));
    } catch (error) {
      console.error("Error marking as interested:", error);
    }
  };

  const toggleSaved = async (professor) => {
    const professorId = professor.id;
    if (guestPreview) {
      setShowAuthModal(true);
      return;
    }
    if (savedProfessors.has(professorId)) {
      await unmarkSaved(professorId);
      return;
    }
    await markSaved(professorId);
  };

  const markSaved = async (professorId) => {
    setSavedProfessors((prev) => new Set(prev).add(professorId));
    try {
      await api.post("/handle-interaction/", {
        professor_id: professorId,
        interaction_type: "saved",
      });
    } catch (error) {
      console.error("Error saving professor:", error);
      setSavedProfessors((prev) => {
        const next = new Set(prev);
        next.delete(professorId);
        return next;
      });
      throw error;
    }
  };

  const unmarkSaved = async (professorId) => {
    setSavedProfessors((prev) => {
      const next = new Set(prev);
      next.delete(professorId);
      return next;
    });
    try {
      await api.post("/unmark-saved/", { professor_id: professorId });
    } catch (error) {
      console.error("Error unsaving professor:", error);
      setSavedProfessors((prev) => new Set(prev).add(professorId));
      throw error;
    }
  };

  // Latest snapshot for unmount write (must run before early return so we never save stale/empty state)
  cacheSnapshotRef.current = {
    recommendations,
    interestedProfessors,
    savedProfessors,
    recentProfessorIds,
  };

  // Only block the UI on the very first load with no rows yet. Cached restores always render immediately.
  const showInitialLoading = loading && recommendations.length === 0;

  if (showInitialLoading) {
    return <p>Loading... Please wait.</p>;
  }

  return (
    <div className="flex flex-col nav:flex-row h-screen">
      <Sidebar
        guestPreview={guestPreview}
        onGuestRequireAuth={() => setShowAuthModal(true)}
      />

      <div className="flex-1 flex flex-col overflow-x-hidden p-4 nav:p-6 px-4 nav:px-8">
        <h2 className="text-2xl nav:text-3xl font-semibold text-gray-800">
          Professor Recommendations
        </h2>
        {guestPreview && (
          <p className="mt-2 text-sm text-gray-600">
            Preview mode — sign up to save professors and get more recommendations.
          </p>
        )}
        {errorMessage && <p className="text-red-500">{errorMessage}</p>}

        <div className="recommendations-list mt-4 nav:mt-6">
          {recommendations.map((professor) => (
            <RecommendationCard
              key={professor.id}
              professor={professor}
              isInterested={interestedProfessors.has(professor.id)}
              isSaved={savedProfessors.has(professor.id)}
              onSave={() => toggleSaved(professor)}
              onExpand={() => markInterested(professor.id)}
              guestPreview={guestPreview}
              onGuestRequireAuth={() => setShowAuthModal(true)}
            />
          ))}
        </div>

        <div id="scroll-anchor" className="mt-4 nav:mt-6">
          {guestPreview ? (
            <div className="flex flex-col items-center py-8">
              <p className="text-gray-600 mb-4">Want more matches?</p>
              <button
                type="button"
                onClick={() => setShowAuthModal(true)}
                className="rounded-full bg-[#0196BF] px-6 py-3 font-semibold text-white hover:bg-[#017BA3]"
              >
                Create a free account to continue
              </button>
            </div>
          ) : (
            loadingMore && (
              <div className="flex justify-center">
                <CustomLoader />
              </div>
            )
          )}
        </div>
      </div>

      <SignUpToContinueModal
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        researchInterests={researchInterestsForModal}
      />
    </div>
  );
};

export default Recommendations;
